import { Link } from 'react-router-dom'
import { Card } from '../ui/Card'
import { SkeletonCard } from '../ui/Skeleton'
import type { DashboardSalesStats } from '../../services/dashboardService'
import { formatMoney, resolveCurrencyCode } from '../../lib/money'
import { dashboardHasSalesData } from '../../lib/dashboardSalesStats'

type MetricCardProps = {
  label: string
  value: string
  hint?: string
  href?: string
}

function MetricCard({ label, value, hint, href }: MetricCardProps) {
  const body = (
    <>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-foreground tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </>
  )

  if (href) {
    return (
      <Link to={href} className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Card className="p-5 transition-colors hover:border-primary/30" padding={false}>
          {body}
        </Card>
      </Link>
    )
  }

  return (
    <Card className="p-5" padding={false}>
      {body}
    </Card>
  )
}

type DashboardSalesMetricsProps = {
  stats: DashboardSalesStats | null
  loading: boolean
  error: string | null
}

export function DashboardSalesMetrics({ stats, loading, error }: DashboardSalesMetricsProps) {
  if (loading) {
    return (
      <section aria-busy="true" aria-label="Loading business metrics">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Business health</h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-foreground">Business health</h3>
        <p className="mt-2 text-sm text-destructive">{error}</p>
      </Card>
    )
  }

  const currency = resolveCurrencyCode(stats?.currency)
  const hasData = dashboardHasSalesData(stats)

  if (!hasData) {
    return (
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-foreground">Business health</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          No orders or revenue in the last 30 days yet. When customers place orders or you record in-store sales, trends
          will appear here.
        </p>
        <Link to="/orders" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
          View orders
        </Link>
      </Card>
    )
  }

  return (
    <section>
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Business health</h3>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="Orders (7 days)" value={String(stats!.ordersLast7Days)} hint="Excludes cancelled" />
        <MetricCard label="Orders (30 days)" value={String(stats!.ordersLast30Days)} hint="Excludes cancelled" />
        <MetricCard
          label="Revenue (7 days)"
          value={formatMoney(stats!.revenueLast7Days, currency)}
          hint="Online + in-store"
        />
        <MetricCard
          label="Revenue (30 days)"
          value={formatMoney(stats!.revenueLast30Days, currency)}
          hint="Online + in-store"
        />
        <MetricCard
          label="Awaiting fulfillment"
          value={String(stats!.pendingFulfillmentCount)}
          hint="Processing through shipped"
          href="/orders"
        />
      </div>
    </section>
  )
}
